import { EnvironmentVariables } from "@/modules/common/types";
import { muiTheme } from "@/styles/theme";
import { Button } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { Home, Images, Pencil, User, Move } from "lucide-react";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import { useContext } from "react";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import useRenderPageComponent from "@/modules/common/hooks/render-page-component.hook";

interface ListingsActionsMenuProps {
  setOpenCarousel: (open: boolean) => void;
  setOpenEditModal: (open: boolean) => void;
  setOpenMoveModal: (open: boolean) => void;
  compoundId: number | string;
  websiteListingId: number | string | null;
  trackUid: string;
  trackTypeId?: number;
}

export const ListingsActionsMenu = ({
  setOpenCarousel,
  setOpenEditModal,
  setOpenMoveModal,
  compoundId,
  websiteListingId,
  trackUid,
  trackTypeId,
}: ListingsActionsMenuProps) => {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const { renderedComponent } = useRenderPageComponent({
    page: "leads-new",
    component: "edit-listing",
    trackTypeId,
  });

  const cannotViewPropertyTracks = userHasPermission(
    {
      name: PermissionsEnum.HIDE_PROPERTY_TRACKS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );

  const buttonStyle = {
    color: muiTheme.colorSchemes.light.palette.main.actionBlue,
    textTransform: "capitalize",
    ...muiTheme.typography.action_button,
    "@media (max-width:600px)": {
      ...muiTheme.typography.body_sm_med,
    },
    width: "100%", // Make buttons fill their grid cells
    justifyContent: "flex-start", // Align button content to the left
  };

  return (
    <Grid container spacing={2}>
      <Grid item mobile={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Button
              sx={buttonStyle}
              onClick={() => setOpenCarousel(true)}
              startIcon={<Images width={16} height={15} />}
            >
              Listing Images
            </Button>
          </Grid>
          <Grid item>
            {!cannotViewPropertyTracks && (
              <Button
                sx={buttonStyle}
                href={
                  EnvironmentVariables.CRM_NAVIGATE_URL +
                  `/property-tracks/${trackUid}`
                }
                target="_blank"
                startIcon={<User width={16} height={15} />}
              >
                View Track
              </Button>
            )}
          </Grid>
          <Grid item>
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.MOVE_LISTING,
                  level: PermissionLevel.WRITE,
                },
              ]}
            >
              <Button
                sx={buttonStyle}
                onClick={() => setOpenMoveModal(true)}
                startIcon={<Move width={16} height={15} />}
                disabled={true}
              >
                Move Listing
              </Button>
            </PermissionGuard>
          </Grid>
        </Grid>
      </Grid>
      <Grid item mobile={6}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Button
              sx={buttonStyle}
              href={
                EnvironmentVariables.LISTING_URL +
                `/compound/${compoundId}/property/${websiteListingId}`
              }
              target="_blank"
              startIcon={<Home width={16} height={15} />}
            >
              View Unit
            </Button>
          </Grid>
          <Grid item>
            {renderedComponent && (
              <Button
                onClick={() => setOpenEditModal(true)}
                sx={buttonStyle}
                startIcon={<Pencil width={16} height={15} />}
              >
                Edit
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
